/**
 * Site-scoped media & analytics tags (SPA-safe).
 *
 * The legacy PHP site fired these tags from `head.php` / `footer.php` on every
 * server page render. This SPA calls `analytics()` (and therefore
 * `initMediaTags()`) on every client-side route render, so naive ports would
 * re-inject the external loaders and double-fire events. A per-site guard
 * object (`window.__financialaidJsb`, mirroring the JSB guard pattern) keeps
 * each external `<script>` to a single injection while letting the page-view
 * beacons fire once per route (matching the legacy per-page-load behavior).
 *
 * Tag IDs are carried over verbatim from the decommissioned legacy repo
 * `codecommit/www.financialaidantitrustsettlement.com`.
 *
 * Page-load tags also include the "568 Group Cartel — Remaining Defendants"
 * media campaign (Ops Scripts CSV): two additional GA4 properties and a Google
 * Ads account, configured off the same single gtag loader.
 *
 * Conversion events (`fireLeadConversion`, `fireAdsPageLoadConversion`) are
 * ported but intentionally left unwired: the legacy Lead bundle and the
 * campaign's Google Ads conversions target a form/submit surface, and this site
 * has none (no migrated form; submit-claim suppressed). Wire them into the
 * relevant form success/mount path when claims/forms open.
 *
 * @module mediaTags
 */

/** Google Analytics measurement IDs. */
const GA_SWATJS = "G-WKXC7VDFFT";
const GA_GENERAL = "G-LGF2TN8VN9";
const GA_CASE = "G-732TVEVG98";
// 568 Group Cartel — Remaining Defendants media campaign (Ops Scripts CSV).
const GA_CAMPAIGN = "G-PC492EW7L9";
const GA_JSB = "G-WGZ6WHWEEN";

/**
 * Google Ads (Remaining Defendants campaign). Conversions are ported unwired —
 * this informational site has no form/submit surface (claims suppressed). Wire
 * them to a form success/click path when one is added.
 *
 * NOTE: the page-load label is used verbatim from the Ops CSV; confirm it
 * against the Google Ads account before wiring — brief [1349] recorded the same
 * account's page-load label without the hyphen, so one is a transcription
 * artifact.
 */
const GOOGLE_ADS_ACCOUNT = "AW-10839088199";
const ADS_PAGE_LOAD_CONVERSION = "AW-10839088199/S-HVCOTLw5ADEMe4vbAo";
const ADS_SUBMIT_CONVERSION = "AW-10839088199/-X6MCN7agYsbEMe4vbAo";

/** Facebook Pixel. */
const FB_PIXEL_ID = "786888246669925";
const FB_EVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js";

/** The Trade Desk (adsrvr.org) Universal Pixel. */
const TTD_LOADER_SRC = "https://js.adsrvr.org/up_loader.1.1.0.js";
const TTD_ADVERTISER = "007fhoe";
const TTD_TAG = "xvmliqc";
const TTD_ENDPOINT = "https://insight.adsrvr.org/track/up";
const TTD_CONVERSION_PXL =
    "https://insight.adsrvr.org/track/pxl/?adv=007fhoe&ct=0:k38abuq&fmt=3";

/** RocketFuel / RFI (rfihub) DMP conversion tracking (version 9). */
const RFI_SRC = "https://c1.rfihub.net/js/tc.min.js";
const RFI_VERSION = "9";
const RFI_REVENUE_BLOCK = "46371";
const RFI_PAGE_ACTION = "20855572";
const RFI_CONVERSION_ACTION = "20855573";

/**
 * Lazily create and return the per-site guard object. Keys are set as each
 * one-time action completes so repeated route renders are no-ops.
 *
 * @returns {Object} The mutable guard object on `window`.
 */
function guards() {
    window.__financialaidJsb = window.__financialaidJsb || {};
    return window.__financialaidJsb;
}

/**
 * Inject an external script exactly once per session (guarded). On subsequent
 * calls the optional `onload` callback is invoked directly, since the script is
 * already present — this lets per-route beacons re-fire without re-injecting.
 *
 * If the injected script errors (CSP, ad blocker, transient network), the guard
 * is cleared and the dead node removed, so a later route render can retry the
 * injection rather than leaving the tag disabled for the rest of the session.
 *
 * @param {string} src - The script URL.
 * @param {string} guardKey - The guard key tracking this injection.
 * @param {Function} [onload] - Optional callback run on load / on later calls.
 * @returns {void}
 */
function loadScriptOnce(src, guardKey, onload) {
    const g = guards();
    if (g[guardKey]) {
        if (typeof onload === "function") {
            onload();
        }
        return;
    }
    g[guardKey] = true;
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    if (typeof onload === "function") {
        script.addEventListener("load", onload);
    }
    script.addEventListener("error", () => {
        delete g[guardKey];
        script.remove();
    });
    document.head.appendChild(script);
}

/**
 * Load gtag.js once and configure each GA4 property and the Google Ads account
 * once. The gtag stub is defined on `window` so the conversion helpers can use
 * it later.
 *
 * @returns {void}
 */
function initGtag() {
    const g = guards();
    loadScriptOnce(
        "https://www.googletagmanager.com/gtag/js?id=" + GA_GENERAL,
        "gtagScriptLoaded"
    );
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
    }
    if (!g.gtagConfigured) {
        window.gtag("js", new Date());
        window.gtag("config", GA_SWATJS);
        window.gtag("config", GA_GENERAL);
        window.gtag("config", GA_CASE);
        window.gtag("config", GA_CAMPAIGN);
        window.gtag("config", GA_JSB);
        window.gtag("config", GOOGLE_ADS_ACCOUNT);
        g.gtagConfigured = true;
    }
}

/**
 * Define the Facebook Pixel `fbq` stub and inject `fbevents.js` once. The stub
 * itself short-circuits if `fbq` already exists, so this is safe to call on
 * every route.
 *
 * @returns {void}
 */
function bootstrapFbq() {
    const w = window;
    if (w.fbq) {
        return;
    }
    const fbq = function () {
        if (fbq.callMethod) {
            fbq.callMethod.apply(fbq, arguments);
        } else {
            fbq.queue.push(arguments);
        }
    };
    w.fbq = fbq;
    if (!w._fbq) {
        w._fbq = fbq;
    }
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    loadScriptOnce(FB_EVENTS_SRC, "fbeventsLoaded");
}

/**
 * Initialize the Facebook Pixel once and fire a PageView per route render.
 *
 * @returns {void}
 */
function initMetaPixel() {
    const g = guards();
    bootstrapFbq();
    if (!g.metaPixelInit) {
        window.fbq("init", FB_PIXEL_ID);
        g.metaPixelInit = true;
    }
    window.fbq("track", "PageView");
}

/**
 * Fire the Trade Desk Universal Pixel, guarding on the loader being ready.
 *
 * @returns {void}
 */
function fireTtdUniversalPixel() {
    const w = window;
    if (
        typeof w.ttd_dom_ready !== "function" ||
        typeof w.TTDUniversalPixelApi !== "function"
    ) {
        return;
    }
    w.ttd_dom_ready(function () {
        const universalPixelApi = new w.TTDUniversalPixelApi();
        universalPixelApi.init(TTD_ADVERTISER, [TTD_TAG], TTD_ENDPOINT);
    });
}

/**
 * Load the Trade Desk pixel loader once; fire the universal pixel on load and
 * on subsequent route renders.
 *
 * @returns {void}
 */
function initTradeDesk() {
    loadScriptOnce(TTD_LOADER_SRC, "ttdLoaded", fireTtdUniversalPixel);
}

/**
 * Fire an RFI tracking call for the given conversion-action id. The `_rfi` stub
 * is defined inline (independent of the external script) so queued commands are
 * processed once `tc.min.js` loads.
 *
 * @param {string} action - The RFI conversion-action id (`ca` / `_t`).
 * @returns {void}
 */
function fireRfi(action) {
    const w = window;
    if (typeof w._rfi !== "function") {
        w._rfi = function () {
            w._rfi.commands = w._rfi.commands || [];
            w._rfi.commands.push(arguments);
        };
    }
    w._rfi("setArgs", "ver", RFI_VERSION);
    w._rfi("setArgs", "rb", RFI_REVENUE_BLOCK);
    w._rfi("setArgs", "ca", action);
    w._rfi("setArgs", "_o", RFI_REVENUE_BLOCK);
    w._rfi("setArgs", "_t", action);
    w._rfi("track");
}

/**
 * Load the RFI loader once and fire the page-load tracking call per route.
 *
 * @returns {void}
 */
function initRfiPageTag() {
    loadScriptOnce(RFI_SRC, "rfiLoaded");
    fireRfi(RFI_PAGE_ACTION);
}

/**
 * Initialize all page-load media/analytics tags (SPA-safe). Call on every route
 * render; external loaders inject once and page-view beacons fire per route.
 *
 * @returns {void}
 */
export function initMediaTags() {
    initGtag();
    initMetaPixel();
    initTradeDesk();
    initRfiPageTag();
}

/**
 * Fire the conversion bundle (Facebook Lead, GA generate_lead, Trade Desk and
 * RFI conversion beacons), each once per session. Safe to call before the
 * loaders exist — the `fbq` / `gtag` beacons retry on a later call if their
 * globals are not yet defined. Currently unwired; attach to a form success path
 * when claims/forms open.
 *
 * @returns {void}
 */
export function fireLeadConversion() {
    const g = guards();

    if (!g.metaLeadFired && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
        g.metaLeadFired = true;
    }

    if (!g.gtagLeadFired && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", { currency: "USD", value: 1 });
        g.gtagLeadFired = true;
    }

    if (!g.adsSubmitConversionFired && typeof window.gtag === "function") {
        window.gtag("event", "conversion", { send_to: ADS_SUBMIT_CONVERSION });
        g.adsSubmitConversionFired = true;
    }

    if (!g.ttdConversionFired) {
        const imgPxl = document.createElement("img");
        imgPxl.style.display = "none";
        imgPxl.src = TTD_CONVERSION_PXL;
        document.body.appendChild(imgPxl);
        g.ttdConversionFired = true;
    }

    if (!g.rfiConversionFired) {
        loadScriptOnce(RFI_SRC, "rfiLoaded");
        fireRfi(RFI_CONVERSION_ACTION);
        g.rfiConversionFired = true;
    }
}

/**
 * Fire the Google Ads page-load conversion once (guarded). Ported unwired: the
 * Ops CSV places this on the form-page load, which does not exist on this
 * informational site. Call from a form page's mount when one is added. Safe to
 * call before `gtag` is defined — it no-ops until the loader has run.
 *
 * @returns {void}
 */
export function fireAdsPageLoadConversion() {
    const g = guards();
    if (g.adsPageLoadConversionFired || typeof window.gtag !== "function") {
        return;
    }
    window.gtag("event", "conversion", { send_to: ADS_PAGE_LOAD_CONVERSION });
    g.adsPageLoadConversionFired = true;
}
